import React, { useEffect, useState } from "react";
import { Plus, CircleDollarSign, Banknote, ListChecks } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtMoney, fmtDate, EmptyState } from "../components/ui";

export default function Loans() {
  const { user } = useAuth();
  const [tab, setTab] = useState("loans");
  const [data, setData] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(null); // 'loan' | 'bonus'
  const [form, setForm] = useState({ employeeId: "", type: "LOAN", amount: "", tenureMonths: 12, reason: "" });
  const [disburse, setDisburse] = useState(null); // loan obj
  const [disForm, setDisForm] = useState({ interestRate: 10, tenureMonths: 12 });
  const [schedule, setSchedule] = useState(null); // {loan, data}
  const [busy, setBusy] = useState(false);
  const canManage = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/loans").then(({ data }) => setData(data));
  useEffect(() => { load(); api.get("/employees?limit=100").then(({ data }) => setEmps(data.items)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (open === "loan") await api.post("/loans", form);
    else await api.post("/bonuses", { employeeId: form.employeeId, type: form.type, amount: form.amount, reason: form.reason });
    setOpen(null); setForm({ employeeId: "", type: "LOAN", amount: "", tenureMonths: 12, reason: "" }); load();
  };
  const decide = async (id, status) => { await api.put(`/loans/${id}`, { status }); load(); };
  const openDisburse = (l) => { setDisburse(l); setDisForm({ interestRate: l.interestRate || 10, tenureMonths: l.tenureMonths || 12 }); };
  const submitDisburse = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/loans/${disburse.id}/disburse`, { interestRate: +disForm.interestRate, tenureMonths: +disForm.tenureMonths }); setDisburse(null); load(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const openSchedule = async (l) => {
    try { const { data } = await api.get(`/loans/${l.id}/schedule`); setSchedule({ loan: l, ...data }); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); }
  };
  const repay = async () => {
    setBusy(true);
    try { await api.post(`/loans/${schedule.loan.id}/repay`); const { data } = await api.get(`/loans/${schedule.loan.id}/schedule`); setSchedule((s) => ({ ...s, ...data })); load(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data) return <Loading />;

  return (
    <div>
      <PageHeader title="Loans & Bonuses" subtitle="Advances, EMI-based loans and rewards">
        {canManage && <>
          <button data-testid="add-loan" onClick={() => { setForm({ employeeId: "", type: "LOAN", amount: "", tenureMonths: 12, reason: "" }); setOpen("loan"); }} className="btn-outline"><Plus size={15} /> Loan</button>
          <button data-testid="add-bonus" onClick={() => { setForm({ employeeId: "", type: "PERFORMANCE", amount: "", reason: "" }); setOpen("bonus"); }} className="btn-accent"><Plus size={16} /> Bonus</button>
        </>}
      </PageHeader>
      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {[["loans", "Loans & Advances"], ["bonuses", "Bonuses"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>
      {tab === "loans" && (data.loans.length === 0 ? <EmptyState icon={CircleDollarSign} title="No loans" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="loans-table">
            <thead><tr><th className="th">Employee</th><th className="th">Type</th><th className="th">Amount</th><th className="th">EMI</th><th className="th">Outstanding</th><th className="th">Status</th><th className="th">Action</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.loans.map((l) => (
                <tr key={l.id} data-testid={`loan-${l.id}`}>
                  <td className="td">
                    <p className="font-medium text-slate-800 dark:text-slate-100">{l.employeeName}</p>
                    {l.reason && <p className="max-w-[200px] truncate text-xs text-slate-400">{l.reason}</p>}
                  </td>
                  <td className="td"><Badge tone="info">{l.type}</Badge></td>
                  <td className="td font-mono font-semibold">{fmtMoney(l.amount)}</td>
                  <td className="td font-mono text-sm">{l.emi ? `${fmtMoney(l.emi)}/mo` : "—"}</td>
                  <td className="td font-mono text-sm">{["DISBURSED", "CLOSED"].includes(l.status) ? fmtMoney(l.outstanding ?? 0) : "—"}</td>
                  <td className="td"><Badge value={l.status} /></td>
                  <td className="td">
                    <div className="flex flex-wrap gap-1.5">
                      {canManage && l.status === "PENDING" && <>
                        <button data-testid={`loan-approve-${l.id}`} onClick={() => decide(l.id, "APPROVED")} className="rounded bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15">Approve</button>
                        <button data-testid={`loan-reject-${l.id}`} onClick={() => decide(l.id, "REJECTED")} className="rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 dark:bg-red-500/15">Reject</button>
                      </>}
                      {canManage && l.status === "APPROVED" && <button data-testid={`loan-disburse-${l.id}`} onClick={() => openDisburse(l)} className="inline-flex items-center gap-1 rounded bg-accent/10 px-2 py-1 text-xs font-semibold text-accent"><Banknote size={13} /> Disburse</button>}
                      {["DISBURSED", "CLOSED"].includes(l.status) && <button data-testid={`loan-schedule-${l.id}`} onClick={() => openSchedule(l)} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300"><ListChecks size={13} /> EMI Schedule</button>}
                      {!canManage && l.status === "PENDING" && <span className="text-xs text-slate-400">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      ))}
      {tab === "bonuses" && (data.bonuses.length === 0 ? <EmptyState icon={CircleDollarSign} title="No bonuses" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="bonuses-table">
            <thead><tr><th className="th">Employee</th><th className="th">Type</th><th className="th">Amount</th><th className="th">Reason</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.bonuses.map((b) => (
                <tr key={b.id}><td className="td font-medium text-slate-800 dark:text-slate-100">{b.employeeName}</td><td className="td"><Badge tone="ok">{b.type}</Badge></td><td className="td font-mono font-semibold text-emerald-600">{fmtMoney(b.amount)}</td><td className="td">{b.reason}</td></tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      ))}

      {/* New loan / bonus */}
      <Modal open={!!open} onClose={() => setOpen(null)} title={open === "loan" ? "New Loan / Advance" : "Grant Bonus"}
        footer={<><button onClick={() => setOpen(null)} className="btn-outline">Cancel</button><button form="ln-form" className="btn-accent">Save</button></>}>
        <form id="ln-form" onSubmit={submit} className="space-y-4">
          <div><label className="label">Employee *</label><select required data-testid="ln-emp" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className="input"><option value="">— Select —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">{(open === "loan" ? ["LOAN", "ADVANCE"] : ["PERFORMANCE", "FESTIVAL", "RETENTION"]).map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="label">Amount (₹) *</label><input required type="number" data-testid="ln-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input" /></div>
          </div>
          {open === "loan" && <div><label className="label">Tenure (months)</label><input type="number" value={form.tenureMonths} onChange={(e) => setForm({ ...form, tenureMonths: +e.target.value })} className="input" /></div>}
          <div><label className="label">Reason</label><textarea data-testid="ln-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="input h-20 py-2" /></div>
        </form>
      </Modal>

      {/* Disburse */}
      <Modal open={!!disburse} onClose={() => setDisburse(null)} title="Disburse Loan"
        footer={<><button onClick={() => setDisburse(null)} className="btn-outline">Cancel</button><button form="dis-form" disabled={busy} className="btn-accent">Disburse & Generate EMI</button></>}>
        <form id="dis-form" onSubmit={submitDisburse} className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">Disbursing {fmtMoney(disburse?.amount)} to <b>{disburse?.employeeName}</b>. An amortization (EMI) schedule will be generated.</p>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Interest Rate (% p.a.)</label><input type="number" step="0.1" data-testid="dis-rate" value={disForm.interestRate} onChange={(e) => setDisForm({ ...disForm, interestRate: e.target.value })} className="input" /></div>
            <div><label className="label">Tenure (months)</label><input type="number" data-testid="dis-tenure" value={disForm.tenureMonths} onChange={(e) => setDisForm({ ...disForm, tenureMonths: e.target.value })} className="input" /></div>
          </div>
        </form>
      </Modal>

      {/* EMI Schedule */}
      <Modal open={!!schedule} onClose={() => setSchedule(null)} title={`EMI Schedule — ${schedule?.loan?.employeeName || ""}`}
        footer={<>
          <button onClick={() => setSchedule(null)} className="btn-outline">Close</button>
          {canManage && schedule?.loan?.status === "DISBURSED" && <button data-testid="loan-repay" disabled={busy} onClick={repay} className="btn-accent">Record EMI Payment</button>}
        </>}>
        {schedule && <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["EMI", `${fmtMoney(schedule.emi)}`], ["Rate", `${schedule.interestRate}%`], ["Tenure", `${schedule.tenureMonths} mo`], ["Outstanding", fmtMoney(schedule.outstanding ?? 0)]].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{k}</p><p className="mt-0.5 font-mono text-sm font-bold text-slate-800 dark:text-slate-100">{v}</p></div>
            ))}
          </div>
          <p className="text-xs text-slate-400">Total interest over tenure: <b>{fmtMoney(schedule.totalInterest)}</b></p>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800">
            <table className="w-full" data-testid="emi-schedule-table">
              <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900"><tr><th className="th">#</th><th className="th">EMI</th><th className="th">Principal</th><th className="th">Interest</th><th className="th">Balance</th><th className="th">Status</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {(schedule.schedule || []).map((s) => (
                  <tr key={s.month} className={s.paid ? "bg-emerald-50/50 dark:bg-emerald-500/5" : ""}>
                    <td className="td text-sm">{s.month}</td>
                    <td className="td font-mono text-sm">{fmtMoney(s.emi)}</td>
                    <td className="td font-mono text-sm">{fmtMoney(s.principal)}</td>
                    <td className="td font-mono text-sm text-slate-400">{fmtMoney(s.interest)}</td>
                    <td className="td font-mono text-sm">{fmtMoney(s.balance)}</td>
                    <td className="td">{s.paid ? <Badge tone="ok">PAID</Badge> : <Badge tone="info">DUE</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>}
      </Modal>
    </div>
  );
}
